import { UniverseId } from '@/domain/universes/universe.interface';
import { SenAnalysisFeatureService } from '@/features/sen-analysis/sen-analysis-feature.service';
import { UiIconButtonComponent } from '@/ui/icon-button/ui-icon-button';
import { ReferenceListLabelPipe } from '@/utils/reference-lists/pipes/reference-list-label.pipe';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { isEqual } from 'lodash-es';
import { combineLatest, type Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, takeWhile } from 'rxjs/operators';
import type { CoreSensor } from '../../../interfaces/Sensor.interface';
import type { ComfortTileData } from '../../../interfaces/Tile.interface';
import type { SensorTimeseriesId } from '../../../interfaces/Timeseries.interface';
import { UnitCode } from '../../../interfaces/Unit.interface';
import { SharedUiColoredTextColorDirective } from '../../../libs/shared/ui/theming/lib/colors/shared-ui-color.directives';
import { LocalNumberPipe, SignificantDigitPipe } from '../../../pipes/localnumber.pipe';
import { SensorsProvider } from '../../../providers/sensors/sensors.provider';
import { TilesProvider } from '../../../providers/tiles/tiles.provider';
import { AdvizeoModalService } from '../../../services/advizeo-modal.service';
import { EditTextComponent } from '../../form-controls/controls/edit-text/edit-text';
import { MiniWidgetIconCardComponent } from '../../widgets/mini-widgets/mini-widget-icon-card/mini-widget-icon-card';
import { SpawnableTile } from '../spawnable-tile/spawnable-tile.component';

@Component({
    styleUrls: ['./tile-comfort.scss'],
    selector: 'tile-comfort',
    templateUrl: 'tile-comfort.html',
    imports: [
        MiniWidgetIconCardComponent,
        EditTextComponent,
        FormsModule,
        UiIconButtonComponent,
        SharedUiColoredTextColorDirective,
        AsyncPipe,
        TranslatePipe,
        LocalNumberPipe,
        SignificantDigitPipe,
        ReferenceListLabelPipe
    ]
})
export class TileComfortComponent extends SpawnableTile<ComfortTileData> {
    private _sensorsProvider = inject(SensorsProvider);
    private _advizeoModalService = inject(AdvizeoModalService);
    private _translateService = inject(TranslateService);
    private _tilesProvider = inject(TilesProvider);
    private _senAnalysisService = inject(SenAnalysisFeatureService);

    temperatureSensor$!: Observable<CoreSensor>;
    humiditySensor$!: Observable<CoreSensor>;

    isInsideThreshold$!: Observable<boolean>;
    isTemperatureInsideThreshold$!: Observable<boolean>;
    isHumidityInsideThreshold$!: Observable<boolean>;

    temperatureUnit: UnitCode = UnitCode.CELSIUS;

    loading$!: Observable<boolean>;

    ngOnInit() {
        const temperatureTileSensor = this.tileData.description.sensors.find(sensor => sensor.universeId === UniverseId.TEMPERATURE);
        const humidityTileSensor = this.tileData.description.sensors.find(sensor => sensor.universeId === UniverseId.HUMIDITY);

        const sensors$ = this._sensorsProvider
            .getSensorsByTimeseriesIds$(this.tileData.description.sensors.map(sensor => sensor.timeseriesId))
            .pipe(
                distinctUntilChanged<CoreSensor[]>(isEqual),
                filter(sensors => sensors.every(sensor => !!sensor))
            );

        this.loading$ = sensors$.pipe(
            map(() => false),
            startWith(true),
            takeWhile(isLoading => !isLoading)
        );

        this.temperatureSensor$ = sensors$.pipe(
            map(sensors => sensors.find(sensor => sensor.timeseriesId === temperatureTileSensor!.timeseriesId)!)
        );
        this.isTemperatureInsideThreshold$ = this.temperatureSensor$.pipe(
            filter<CoreSensor>(isNotNullish),
            filter(sensor => !!sensor.lastPoint),
            map(sensor =>
                this.isTemperatureInsideThreshold(sensor.lastPoint!.value, temperatureTileSensor!.min, temperatureTileSensor!.max)
            )
        );

        if (humidityTileSensor) {
            this.humiditySensor$ = sensors$.pipe(
                map(sensors => sensors.find(sensor => sensor.timeseriesId === humidityTileSensor.timeseriesId)!)
            );
            this.isHumidityInsideThreshold$ = combineLatest([this.temperatureSensor$, this.humiditySensor$]).pipe(
                filter(sensors => sensors.every(sensor => !!sensor && !!sensor.lastPoint)),
                map(([temperature, humidity]) => this.isHumidityInsideThreshold(temperature.lastPoint!.value, humidity.lastPoint!.value))
            );

            this.isInsideThreshold$ = combineLatest([this.isTemperatureInsideThreshold$, this.isHumidityInsideThreshold$]).pipe(
                map(
                    ([isTemperatureInsideThreshold, isHumidityInsideThreshold]) => isTemperatureInsideThreshold && isHumidityInsideThreshold
                )
            );
        } else {
            this.isInsideThreshold$ = this.isTemperatureInsideThreshold$;
        }
    }

    async goToAnalysisPage() {
        const timeseriesIds: SensorTimeseriesId[] = this.tileData.description.sensors.map(sensor => sensor.timeseriesId);

        this._senAnalysisService.goToAnalysis(this.tileData.buildingId, timeseriesIds);
    }

    deleteTile(ev: Event) {
        ev.preventDefault();
        ev.stopPropagation();

        this._advizeoModalService.openLegacyDialog({
            title: this._translateService.instant('WIDGET.HOME_TILES.DELETE_CONFIRMATION_TITLE'),
            message: this._translateService.instant('WIDGET.HOME_TILES.DELETE_CONFIRMATION_MESSAGE', { tileName: this.tileData.name }),
            buttons: [
                {
                    text: this._translateService.instant('BUTTONS.CANCEL'),
                    role: 'cancel'
                },
                {
                    text: this._translateService.instant('BUTTONS.DELETE'),
                    handler: () => {
                        this.processDeleteTile();
                    }
                }
            ]
        });
    }

    updateTileName() {
        this._tilesProvider.renameTile(this.tileData.tileId, this.tileData.name);
    }

    private processDeleteTile() {
        this._tilesProvider.deleteTile(this.tileData.tileId, this.tileData.buildingId).then(() => {
            this.deleted.emit();
        });
    }

    isTemperatureInsideThreshold(value: number, min: number, max: number): boolean {
        return value <= max && value >= min;
    }

    isHumidityInsideThreshold(temperatureValue: number, humidityValue: number): boolean {
        // 5% error margin on humidity and 0.2 Celsius degree error on temperature
        const lowerCoefficients = [-1.06, 55.9]; // a0, b0
        const lowerFunction = lowerCoefficients[0] * (temperatureValue + 0.2) + lowerCoefficients[1];

        const upperCoefficients = [-3.5, 143.5]; // a1, b1
        const upperFunction = upperCoefficients[0] * (temperatureValue + 0.2) + upperCoefficients[1];

        return humidityValue >= lowerFunction && humidityValue <= upperFunction;
    }
}
